import { useEffect } from 'react';
import { AppShell, Button, Group, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import type { Actor, PolicyStatus } from '@prooffleet/contracts';
import { api } from '@prooffleet/client';
import { StatusBar } from './components/StatusBar.js';
import { Briefing } from './screens/Briefing.js';
import { Done } from './screens/Done.js';
import { History } from './screens/History.js';
import { Inspection } from './screens/Inspection.js';
import { Login } from './screens/Login.js';
import { Policy } from './screens/Policy.js';
import { UnitSelect } from './screens/UnitSelect.js';
import { useSession } from '@prooffleet/client';

interface MePayload {
  actor: Actor;
  policy: PolicyStatus;
}

export function App(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken, actor, policyAcknowledged, setSession, setPolicyAcknowledged, clear } =
    useSession();

  // Восстановление сессии по refresh-куке: приложение открывают с домашнего
  // экрана, и водитель не должен вводить PIN каждое утро заново.
  const { data, isLoading } = useQuery({
    queryKey: ['me', accessToken],
    queryFn: () => api<MePayload>('/auth/me'),
    retry: false,
  });

  useEffect(() => {
    if (data === undefined) return;
    setSession(useSession.getState().accessToken ?? '', data.actor);
    setPolicyAcknowledged(!data.policy.acknowledgementRequired);
  }, [data, setSession, setPolicyAcknowledged]);

  const logout = async (): Promise<void> => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      clear();
      void navigate('/');
    }
  };

  if (isLoading && actor === null) {
    return (
      <div className="pf-scroll">
        <Text>{t('common.loading')}</Text>
      </div>
    );
  }

  if (actor === null) return <Login />;
  if (!policyAcknowledged) return <Policy />;

  return (
    <AppShell header={{ height: 56 }} padding={0}>
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Text fw={700}>{t('app.name')}</Text>
          <Group gap="xs" wrap="nowrap">
            <StatusBar />
            <Button variant="subtle" size="compact-sm" onClick={() => void logout()}>
              {t('common.logout')}
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main style={{ height: 'calc(100dvh - 56px)' }}>
        <Routes>
          <Route path="/" element={<Navigate to="/units" replace />} />
          <Route path="/units" element={<UnitSelect />} />
          <Route path="/units/:unitId/briefing" element={<Briefing />} />
          <Route path="/units/:unitId/inspect" element={<Inspection />} />
          <Route path="/done" element={<Done />} />
          <Route path="/history" element={<History />} />
          <Route path="*" element={<Navigate to="/units" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}
